import { Platform } from "react-native";

/**
 * Minimal router interface representing navigation actions.
 */
export interface INavigator {
  canGoBack: () => boolean;
  back: () => void;
  navigate: (href: any, options?: any) => void;
  replace: (href: any, options?: any) => void;
}

/**
 * Minimal navigation interface for React Navigation screen navigation props.
 */
export interface INavigationProp {
  canGoBack?: () => boolean;
  goBack?: () => void;
  getParent?: (id?: string) => any;
  getState?: () => any;
}

/**
 * In-memory chronological history stack tracking path transitions.
 */
let historyStack: string[] = [];

/**
 * Normalizes a route by stripping query strings, grouping prefixes, and trailing slashes.
 *
 * @param {string | null | undefined} rawPath - The raw pathname to normalize.
 * @returns {string} Normalized route string (e.g. "/my-memories/123" or "/").
 */
export function normalizeRoute(rawPath: string | null | undefined): string {
  if (!rawPath) return "/";
  // Remove query parameters or hash
  const withoutQuery = rawPath.split("?")[0].split("#")[0];
  // Remove (tabs) prefix
  const clean = withoutQuery.replace(/^\/\(tabs\)/, "").replace(/\/$/, "");
  return clean === "" ? "/" : clean;
}

/**
 * Determines whether a given route is the root Home screen.
 *
 * @param {string | null | undefined} rawPath - The pathname to inspect.
 * @returns {boolean} True if the route represents Home.
 */
export function isHomeScreen(rawPath: string | null | undefined): boolean {
  const norm = normalizeRoute(rawPath);
  return norm === "/" || norm === "/index";
}

/**
 * Resolves the hierarchical parent route when no chronological history exists.
 *
 * @param {string | null | undefined} rawPath - Current route pathname.
 * @returns {string | null} Canonical parent route, or null if on Home.
 */
export function getParentRoute(rawPath: string | null | undefined): string | null {
  if (isHomeScreen(rawPath)) {
    return null;
  }

  const norm = normalizeRoute(rawPath);
  const segments = norm.split("/").filter(Boolean);

  if (segments.length === 0) {
    return null;
  }

  // 1. Nested child screens under admin
  if (segments[0] === "admin") {
    if (segments.length === 1) {
      return "/";
    }
    if (segments[1] === "design") {
      return segments.length > 2 ? "/admin/design" : "/admin";
    }
    if (segments[1] === "test") {
      return segments.length > 2 ? "/admin/test" : "/admin";
    }
    return "/admin";
  }

  // 2. Shared screens (quota, terminate-account) resolve to profile
  if (
    segments[0] === "(z_shared)" ||
    segments[0] === "quota" ||
    segments[0] === "terminate-account"
  ) {
    return "/profile";
  }

  // 3. Child/edit/new screens under domain tabs (e.g. /my-memories/123 -> /my-memories)
  if (segments.length > 1) {
    return `/${segments[0]}`;
  }

  // 4. Top-level domain list screens (e.g. /my-memories, /my-media, /profile) resolve to Home
  return "/";
}

/**
 * Records a navigation event into the chronological history stack.
 * Prevents redundant consecutive duplicates and enforces a maximum history depth.
 *
 * @param {string | null | undefined} rawPath - The route navigated to.
 */
export function recordNavigation(rawPath: string | null | undefined): void {
  const norm = normalizeRoute(rawPath);
  const currentTop = historyStack[historyStack.length - 1];

  if (currentTop === norm) {
    return;
  }

  historyStack.push(norm);

  // Cap history to 50 items to keep memory footprint bounded
  if (historyStack.length > 50) {
    historyStack.shift();
  }
}

/**
 * Checks if previous chronological history entries exist.
 *
 * @returns {boolean} True if there is at least one preceding page in history.
 */
export function hasPreviousRoute(): boolean {
  return historyStack.length > 1;
}

/**
 * Gets the route immediately preceding the current top of the stack without removing it.
 *
 * @returns {string | null} Preceding route or null if no history exists.
 */
export function getPreviousRoute(): string | null {
  if (historyStack.length > 1) {
    return historyStack[historyStack.length - 2];
  }
  return null;
}

/**
 * Pops the current route from the history stack and returns the target previous route.
 *
 * @returns {string | null} Target route to navigate back to, or null if empty.
 */
export function popPreviousRoute(): string | null {
  if (historyStack.length > 1) {
    historyStack.pop(); // Remove current
    return historyStack[historyStack.length - 1]; // Return new top
  }
  return null;
}

/**
 * Resets the in-memory navigation history. Primarily used for unit testing and sign-out.
 */
export function resetNavigationHistory(): void {
  historyStack = [];
}

/**
 * Returns a defensive copy of the current navigation history stack.
 *
 * @returns {string[]} Copy of chronological route pathnames.
 */
export function getNavigationHistory(): string[] {
  return [...historyStack];
}

/**
 * Navigates back following the unified precedence:
 * 1. Immediate React Navigation stack (`navigation.goBack()`)
 * 2. Router back (`router.back()`) if safe and history exists
 * 3. Chronological in-memory history stack (`popPreviousRoute()`)
 * 4. Explicit fallback route if provided
 * 5. Hierarchical parent route (`getParentRoute(currentPath)`)
 * 6. Root Home (`/(tabs)`)
 *
 * @param {INavigator} router - The active router instance.
 * @param {INavigationProp} [navigation] - Optional React Navigation prop from useNavigation().
 * @param {string} [currentPathname] - Current route pathname.
 * @param {string} [fallbackRoute] - Optional explicit fallback route.
 */
export function navigateBack(
  router: INavigator,
  navigation?: INavigationProp,
  currentPathname?: string,
  fallbackRoute?: string,
): void {
  // 1. Check local stack screen navigation
  if (navigation && typeof navigation.canGoBack === "function" && navigation.canGoBack()) {
    if (typeof navigation.goBack === "function") {
      navigation.goBack();
      // Keep in-memory stack aligned
      if (historyStack.length > 1) {
        historyStack.pop();
      }
      return;
    }
  }

  // 2. Check parent navigator (e.g. Tabs history)
  if (navigation) {
    const parent = typeof navigation.getParent === "function" ? navigation.getParent() : undefined;
    const parentState = parent?.getState?.();
    const hasParentHistory =
      Array.isArray(parentState?.history) && parentState.history.length > 1;

    if (hasParentHistory && typeof router.canGoBack === "function" && router.canGoBack()) {
      router.back();
      if (historyStack.length > 1) {
        historyStack.pop();
      }
      return;
    }
  }

  // 3. Web check: if running on web and historyState indicates idx > 0
  if (Platform.OS === "web" && typeof window !== "undefined") {
    const historyState = window.history?.state;
    if (historyState && typeof historyState.idx === "number" && historyState.idx > 0) {
      if (typeof router.canGoBack === "function" && router.canGoBack()) {
        router.back();
        if (historyStack.length > 1) {
          historyStack.pop();
        }
        return;
      }
    }
  }

  // 4. Chronological in-memory stack traversal
  const prevRoute = popPreviousRoute();
  if (prevRoute) {
    router.replace(prevRoute as any);
    return;
  }

  // 5. Explicit fallback if specified
  if (fallbackRoute) {
    const canonical = normalizeRoute(fallbackRoute);
    router.replace(canonical as any);
    return;
  }

  // 6. Hierarchical parent fallback
  const parent = getParentRoute(currentPathname);
  if (parent) {
    router.replace(parent as any);
    return;
  }

  // 7. Ultimate fallback: Home
  router.replace("/" as any);
}
